import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';

// React and React Native Core imports
import Page from '../../atoms/Page';
import Heading from '../../atoms/Heading';
import FeedFloTable from '../../organisms/FeedFloTable';
import FeedFloDatePicker from '../../molecules/FeedFloDatePicker';
import { CalendarIcon, CircledCheckmarkIcon, RedXIcon } from '../../atoms/Icons';
import ComboboxMulti from '../../atoms/ComboboxMulti';
import Select from '../../atoms/Select';

// 3rd party libraries
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

// Our Own imports
import { DATE_FORMAT_MONTH_DAY, DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE, secondsToDuration } from '../../utils/dates';
import { convertGramsToLargeUnits, weightLargeUnitLabel } from '../../utils/unitConversion';
import useDefaultDateRange from '../../utils/hooks/useDefaultDateRange';
import WebAppContext from '../../utils/webAppContext';
import { useCan, Privilege } from '@feedflo/access-control';
import { useAtomValue } from 'jotai';
import { algorithmVersionAtom } from '../../utils/jotaiAtoms';
import {
  LARGEST_FEEDFRAMES_QUERY,
  LARGEST_FEEDFRAMES_BY_DURATION_QUERY,
  FEEDERS_QUERY,
  FARM_GQL,
  FEED_FRAME_NOTES_QUERY,
} from './queries';

// Relative path imports
import './LargestFeedframesPage.scss';

function LargestFeedframesPage() {
  // --- State hooks ---
  const [dateRange, setDateRange] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedBarnIds, setSelectedBarnIds] = useState([]);
  const [deletedFilter, setDeletedFilter] = useState('all');
  const [sortBy, setSortBy] = useState('mass');

  // --- Atoms and other Hooks ---
  const { canAnywhere, loading: privilegesLoading } = useCan();
  const { isMetric } = useContext(WebAppContext);
  const algorithmVersion = useAtomValue(algorithmVersionAtom);
  const defaultDateRange = useDefaultDateRange(null, 7);

  // --- Navigation/side effects ---
  useEffect(() => {
    if (defaultDateRange) {
      setDateRange(defaultDateRange);
    }
  }, [defaultDateRange]);

  // --- Apollo/GraphQL hooks ---

  // Get farms for filter
  const { data: farmsData } = useQuery(FARM_GQL, {
    skip: privilegesLoading,
  });
  const farms = farmsData?.farm || [];
  const farmOptions = useMemo(() => {
    return farms.map((farm) => ({
      id: farm.id,
      name: farm.name,
    }));
  }, [farms]);

  // Get all farm IDs if none selected (show all)
  const farmIdsForQuery = useMemo(() => {
    if (selectedBarnIds.length === 0) {
      // If no selection, get all farm IDs to show all farms
      return farms.length > 0 ? farms.map((farm) => farm.id) : null;
    }
    return selectedBarnIds.length > 0 ? selectedBarnIds : null;
  }, [selectedBarnIds, farms]);

  // Create order_by based on sortBy selection
  const orderBy = useMemo(() => {
    if (sortBy === 'duration') {
      return [{ duration: 'desc_nulls_last' }];
    }
    // Default to mass_moved_in_grams
    return [{ mass_moved_in_grams: 'desc_nulls_last' }];
  }, [sortBy]);

  // Build feed_frame where clause with deleted filter
  const feedFrameWhere = useMemo(() => {
    const baseWhere = {
      started_at: { _lte: dateRange?.to?.unix() || 0 },
      ended_at: { _gte: dateRange?.from?.unix() || 0 },
      feed_line: {
        farm_id: { _in: farmIdsForQuery || [] },
        farm: { _not: { mock_barn_simulation_setting: { version: { _is_null: false } } } },
      },
    };

    // Add deleted filter if not 'all'
    if (deletedFilter === 'undeleted') {
      baseWhere.deleted_at = { _is_null: true };
    } else if (deletedFilter === 'deleted') {
      baseWhere.deleted_at = { _is_null: false };
    }
    // 'all' - no deleted filter added

    return baseWhere;
  }, [deletedFilter, dateRange, farmIdsForQuery]);

  // Build where clause for feed_frame_analysis with optional algorithm version filter (for mass sort)
  const analysisWhere = useMemo(() => {
    const where = {
      feed_frame: feedFrameWhere,
      deleted_at: { _is_null: true },
    };

    // Only add algorithm_version filter if algorithmVersion is set
    if (algorithmVersion) {
      where.algorithm_version = { _eq: algorithmVersion };
    } else {
      where.is_current_algorithm_version = { _eq: true };
    }

    return where;
  }, [feedFrameWhere, algorithmVersion]);

  // Build where clause for feed_frame (for duration sort) - includes frames without analyses
  const feedFrameWhereForDuration = useMemo(() => {
    const where = { ...feedFrameWhere };
    // For duration sort, we don't filter by algorithm version at the frame level
    // We'll get the latest analysis if it exists
    return where;
  }, [feedFrameWhere]);

  // Query for mass sort (uses feed_frame_analysis as root)
  const {
    loading: loadingMass,
    error: errorMass,
    data: dataMass,
  } = useQuery(LARGEST_FEEDFRAMES_QUERY, {
    variables: {
      analysisWhere: analysisWhere,
      limit: 100,
      orderBy: orderBy,
    },
    skip: sortBy !== 'mass' || !dateRange || privilegesLoading || !farmIdsForQuery || farmIdsForQuery.length === 0,
    onError: (error) => {
      console.error('LargestFeedframes query error:', error);
      Sentry.captureException(error);
    },
  });

  // Query for duration sort (uses feed_frame as root to include frames without analyses)
  const {
    loading: loadingDuration,
    error: errorDuration,
    data: dataDuration,
  } = useQuery(LARGEST_FEEDFRAMES_BY_DURATION_QUERY, {
    variables: {
      feedFrameWhere: feedFrameWhereForDuration,
      analysisWhere: analysisWhere,
      limit: 100,
      orderBy: orderBy,
    },
    skip: sortBy !== 'duration' || !dateRange || privilegesLoading || !farmIdsForQuery || farmIdsForQuery.length === 0,
    onError: (error) => {
      console.error('LargestFeedframesByDuration query error:', error);
      Sentry.captureException(error);
    },
  });

  // Normalize data structure - convert duration query result to match mass query structure
  const data = useMemo(() => {
    if (sortBy === 'duration' && dataDuration?.feed_frame) {
      // Convert feed_frame array to feed_frame_analysis-like structure
      return {
        feed_frame_analysis: dataDuration.feed_frame.map((frame) => ({
          mass_moved_in_grams: frame.feed_frame_analyses?.[0]?.mass_moved_in_grams || 0,
          feed_frame: frame,
        })),
      };
    }
    return dataMass;
  }, [sortBy, dataMass, dataDuration]);

  const loading = sortBy === 'mass' ? loadingMass : loadingDuration;
  const error = sortBy === 'mass' ? errorMass : errorDuration;

  // Get unique feedline IDs for feeder query
  const feedLineIds = useMemo(() => {
    if (!data?.feed_frame_analysis) return [];
    const ids = new Set();
    data.feed_frame_analysis.forEach((analysis) => {
      if (analysis?.feed_frame?.feed_line?.id) {
        ids.add(analysis.feed_frame.feed_line.id);
      }
    });
    return Array.from(ids);
  }, [data]);

  // Get unique feed_frame IDs for notes query
  const feedFrameIds = useMemo(() => {
    if (!data?.feed_frame_analysis) return [];
    const ids = new Set();
    data.feed_frame_analysis.forEach((analysis) => {
      if (analysis?.feed_frame?.id) {
        ids.add(analysis.feed_frame.id);
      }
    });
    return Array.from(ids);
  }, [data]);

  // Query notes to get categories
  const { data: notesData } = useQuery(FEED_FRAME_NOTES_QUERY, {
    variables: {
      feedFrameIds: feedFrameIds,
    },
    skip: feedFrameIds.length === 0,
  });

  // Create a map of feed_frame_id -> latest category
  // Notes are already ordered by created_at desc, so first note per feed_frame_id is the most recent
  const categoryByFeedFrameId = useMemo(() => {
    if (!notesData?.feed_frame_note) return {};
    const categoryMap = {};
    notesData.feed_frame_note.forEach((note) => {
      const frameId = note.feed_frame_id;
      // Only take the first (most recent) note for each feed_frame_id
      if (!categoryMap[frameId]) {
        const category = note.feed_frame_category;
        categoryMap[frameId] = category?.description || category?.name || null;
      }
    });
    return categoryMap;
  }, [notesData]);

  const { data: feedersData } = useQuery(FEEDERS_QUERY, {
    variables: {
      feedLineIds: feedLineIds,
    },
    skip: feedLineIds.length === 0,
  });

  // --- Derived state ---
  // Calculate feeder capacity per feedline
  const feederCapacityByFeedLine = useMemo(() => {
    if (!feedersData?.feeder) return {};
    const capacityMap = {};
    feedersData.feeder.forEach((feeder) => {
      const feedLineId = feeder.feed_line_id;
      if (!capacityMap[feedLineId]) {
        capacityMap[feedLineId] = 0;
      }
      capacityMap[feedLineId] += (feeder.capacity_in_grams || 0) * (feeder.quantity || 0);
    });
    return capacityMap;
  }, [feedersData]);

  // Staff-only access check
  const hasStaffAccess = canAnywhere('feature', Privilege.FEATURE_STAFF_ACCESS);
  if (!privilegesLoading && !hasStaffAccess) {
    return (
      <Page className="LargestFeedframesPage">
        <div className="LargestFeedframesPage-content">
          <div className="LargestFeedframesPage-header">
            <Heading level={3}>Access Denied</Heading>
          </div>
          <p>This page is only available to FeedFlo staff members.</p>
        </div>
      </Page>
    );
  }

  // --- Function definitions ---
  function handleClickedOutside() {
    setShowDatePicker(false);
  }

  function onDateChange(e) {
    if (e.dateRange) {
      const newFrom = e.dateRange.from;
      const newTo = e.dateRange.to;
      setDateRange({ from: newFrom, to: newTo });
    }
    setShowDatePicker(false);
  }

  function getTableRows() {
    if (loading || !data?.feed_frame_analysis) return [];

    return data.feed_frame_analysis
      .map((analysis) => {
        const feedFrame = analysis.feed_frame;
        const feedLine = feedFrame?.feed_line;
        const farm = feedLine?.farm;

        if (!feedFrame || !feedLine || !farm) return null;

        // Farm link
        const farmLink = <Link to={`/b/${farm.id}`}>{farm.name}</Link>;

        // FeedLine link with 5 days on either side
        const feedFrameStart = feedFrame.started_at;
        const feedFrameEnd = feedFrame.ended_at || feedFrameStart;
        const linkStart = dayjs.unix(feedFrameStart).subtract(5, 'days').unix();
        const linkEnd = dayjs.unix(feedFrameEnd).add(5, 'days').unix();
        const feedLineLink = (
          <Link to={`/b/${farm.id}/line/${feedLine.id}?from=${linkStart}&to=${linkEnd}`}>{feedLine.name}</Link>
        );

        // Time
        const time = dayjs.tz(1000 * feedFrameStart).format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE);

        // Duration
        const durationSeconds = feedFrameEnd - feedFrameStart;
        const duration = secondsToDuration(durationSeconds);

        // Size in tons - use analysis if available, otherwise show 0 or —
        const massInGrams = analysis.mass_moved_in_grams || 0;
        const sizeInTons = convertGramsToLargeUnits(isMetric, massInGrams, 2);
        const sizeDisplay = massInGrams > 0 ? `${sizeInTons} ${weightLargeUnitLabel(isMetric)}` : '—';

        // Total feeder capacity
        const feedLineId = feedLine.id;
        const totalCapacity = feederCapacityByFeedLine[feedLineId] || 0;
        const capacityInTons = convertGramsToLargeUnits(isMetric, totalCapacity, 2);
        const capacityDisplay = `${capacityInTons} ${weightLargeUnitLabel(isMetric)}`;

        // Action: Open Feed Frame page in new tab
        const feedFrameLink = (
          <Link to={`/ff/${feedFrame.id}`} target="_blank" rel="noopener noreferrer">
            🔎
          </Link>
        );

        // Deleted status - use icons
        const isDeleted = feedFrame.deleted_at !== null;
        const deletedStatus = isDeleted ? (
          <RedXIcon className="LargestFeedframesPage-statusIcon" title="Deleted" />
        ) : (
          <CircledCheckmarkIcon className="LargestFeedframesPage-statusIcon" title="Active" />
        );

        // Category from notes query
        const categoryDisplay = categoryByFeedFrameId[feedFrame.id] || '—';

        return [
          farmLink,
          feedLineLink,
          time,
          duration,
          sizeDisplay,
          capacityDisplay,
          deletedStatus,
          categoryDisplay,
          feedFrameLink,
        ];
      })
      .filter(Boolean);
  }

  const tableHeaders = [
    { t: 'FARM', s: 'm' },
    { t: 'FEEDLINE', s: 'm' },
    { t: 'TIME', s: 'm' },
    { t: 'DURATION', s: 'm' },
    { t: 'SIZE', s: 'm' },
    { t: 'FEEDER CAPACITY', s: 'm' },
    { t: 'STATUS', s: 'm' },
    { t: 'CATEGORY', s: 'm' },
    { t: 'ACTIONS', s: 'm' },
  ];

  const fromLabel = dateRange?.from?.format(DATE_FORMAT_MONTH_DAY) || '';
  const toLabel = dateRange?.to?.format(DATE_FORMAT_MONTH_DAY) || '';

  const dateRangeInput = (
    <>
      <div
        className="LargestFeedframesPage-dateRangeButton"
        onClick={() => {
          setShowDatePicker(!showDatePicker);
        }}
      >
        <div className="LargestFeedframesPage-dateRangeButtonIcon">
          <CalendarIcon />
        </div>
        <div className="LargestFeedframesPage-dateRangeButtonText">{`${fromLabel} - ${toLabel}`}</div>
      </div>
      {showDatePicker && (
        <FeedFloDatePicker
          onClickedOutside={() => handleClickedOutside()}
          onChange={(e) => onDateChange(e)}
          from={dateRange?.from?.toDate()}
          to={dateRange?.to?.toDate()}
        />
      )}
    </>
  );

  const barnFilter = (
    <div className="LargestFeedframesPage-barnFilter">
      <ComboboxMulti
        items={farmOptions}
        value={selectedBarnIds}
        onChange={(selectedItems) => {
          // ComboboxMulti passes full item objects, extract IDs
          const ids = selectedItems.map((item) => item.id);
          setSelectedBarnIds(ids);
        }}
        emptyPlaceholder="Filter by Barn..."
        label="Barn"
      />
    </div>
  );

  const deletedFilterSelect = (
    <div className="LargestFeedframesPage-deletedFilter">
      <Select
        label="Status"
        value={deletedFilter}
        onChange={(e) => setDeletedFilter(e.target.value)}
        itemList={[
          { id: 'all', name: 'All', value: 'all' },
          { id: 'undeleted', name: 'Only Undeleted', value: 'undeleted' },
          { id: 'deleted', name: 'Only Deleted', value: 'deleted' },
        ]}
      />
    </div>
  );

  const sortSelect = (
    <div className="LargestFeedframesPage-sortSelect">
      <Select
        label="Sort By"
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value)}
        itemList={[
          { id: 'mass', name: 'Largest Mass', value: 'mass' },
          { id: 'duration', name: 'Largest Duration', value: 'duration' },
        ]}
      />
    </div>
  );

  // --- Render ---
  if (error) {
    console.error('Error loading largest feedframes:', error);
    Sentry.captureException(error);
    toast.error('Error loading feedframes data');
    return (
      <Page className="LargestFeedframesPage" titleSegments={['Largest Feedframes']}>
        <div className="LargestFeedframesPage-content">
          <div className="LargestFeedframesPage-header">
            <Heading level={3}>Error</Heading>
            <p>Failed to load feedframes data. Please try again.</p>
            <p>Error: {error.message}</p>
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page className="LargestFeedframesPage" titleSegments={['Largest Feedframes']}>
      <div className="LargestFeedframesPage-content">
        <div className="LargestFeedframesPage-header">
          <Heading level={3}>Largest Feedframes</Heading>
          <div className="LargestFeedframesPage-headerFilters">
            {barnFilter}
            {deletedFilterSelect}
            {sortSelect}
            {dateRangeInput}
          </div>
        </div>

        <div className="LargestFeedframesPage-table">
          <FeedFloTable
            headers={tableHeaders}
            rows={getTableRows()}
            loading={loading}
            itemTotalCount={data?.feed_frame_analysis?.length || 0}
            maxRows={100}
          />
        </div>
      </div>
    </Page>
  );
}

export default LargestFeedframesPage;
